import React, { useEffect, useState } from 'react';
import TextField from "@mui/material/TextField";
import MenuItem from '@mui/material/MenuItem';
import styles from "./Decloration.module.css";
import InputMask from "react-input-mask";
import FileUploadWithPreview from "./FileUploadWithPreview";

// Helper to parse period value from JSON string or object
const parsePeriodValue = (value) => {
    if (!value) return { from: '', to: '' };
    try {
        let parsed;
        if (typeof value === 'string') {
            // Try to parse as JSON
            parsed = JSON.parse(value);
        } else if (typeof value === 'object' && value !== null) {
            // Already an object
            parsed = value;
        } else {
            return { from: '', to: '' };
        }
        
        // Ensure parsed is an object with from/to properties
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return {
                from: parsed.from || '',
                to: parsed.to || ''
            };
        }
        return { from: '', to: '' };
    } catch (error) {
        console.warn('Error parsing period value:', value, error);
        return { from: '', to: '' };
    }
};

// Helper to format date from YYYY-MM-DD to DD.MM.YYYY
const formatDateToRu = (dateStr) => {
    if (!dateStr) return '';
    try {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
    } catch {
        return dateStr;
    }
};

// Helper to format date from DD.MM.YYYY to YYYY-MM-DD
const formatDateFromRu = (dateStr) => {
    if (!dateStr) return '';
    try {
        const [day, month, year] = dateStr.split('.');
        return `${year}-${month}-${day}`;
    } catch {
        return dateStr;
    }
};

const TextFieldComponent = ({ fieldErrors, setAllFieldsFilled, field, fieldValues, onChange, fileChange }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    
    // Period field state - with safe access to field_type
    const isPeriodField = field?.field_type?.type === 'period';
    const periodValue = isPeriodField ? parsePeriodValue(fieldValues?.[field.id]) : null;
    const [periodFrom, setPeriodFrom] = useState(() => {
        if (!isPeriodField || !periodValue?.from) return '';
        try {
            return formatDateToRu(periodValue.from);
        } catch {
            return '';
        }
    });
    const [periodTo, setPeriodTo] = useState(() => {
        if (!isPeriodField || !periodValue?.to) return '';
        try {
            return formatDateToRu(periodValue.to);
        } catch {
            return '';
        }
    });

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
        setIsTouched(true);
    };
    const handleDropdownToggle = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handlePeriodDateChange = (dateType, value) => {
        const processedValue = value.replace(/\s/g, '');
        const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/;
        
        if (dateType === 'from') {
            setPeriodFrom(processedValue);
            // Only update the JSON value if the date is valid
            if (dateRegex.test(processedValue)) {
                const fromDate = formatDateFromRu(processedValue);
                const toDate = periodTo && dateRegex.test(periodTo) ? formatDateFromRu(periodTo) : '';
                const periodJson = JSON.stringify({ from: fromDate, to: toDate });
                const syntheticEvent = { target: { name: field.name, value: periodJson } };
                onChange(syntheticEvent, field.id);
            } else if (!processedValue) {
                // Clear the value if field is empty
                const toDate = periodTo && dateRegex.test(periodTo) ? formatDateFromRu(periodTo) : '';
                const periodJson = JSON.stringify({ from: '', to: toDate });
                const syntheticEvent = { target: { name: field.name, value: periodJson } };
                onChange(syntheticEvent, field.id);
            }
        } else {
            setPeriodTo(processedValue);
            // Only update the JSON value if the date is valid
            if (dateRegex.test(processedValue)) {
                const fromDate = periodFrom && dateRegex.test(periodFrom) ? formatDateFromRu(periodFrom) : '';
                const toDate = formatDateFromRu(processedValue);
                const periodJson = JSON.stringify({ from: fromDate, to: toDate });
                const syntheticEvent = { target: { name: field.name, value: periodJson } };
                onChange(syntheticEvent, field.id);
            } else if (!processedValue) {
                // Clear the value if field is empty
                const fromDate = periodFrom && dateRegex.test(periodFrom) ? formatDateFromRu(periodFrom) : '';
                const periodJson = JSON.stringify({ from: fromDate, to: '' });
                const syntheticEvent = { target: { name: field.name, value: periodJson } };
                onChange(syntheticEvent, field.id);
            }
        }
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        // Удаление пробелов из введенного значения
        const processedValue = value.replace(/\s/g, '');
        // Условия:
        // 1. Если ни letters_only, ни digits_only не установлены, разрешить любой ввод.
        // 2. Если установлен letters_only, разрешить только буквенные символы.
        // 3. Если установлен digits_only, разрешить только цифровые символы.
        // 4. Если и letters_only, и digits_only установлены, разрешить и буквы, и цифры.
        if ((!field.letters_only && !field.digits_only) ||
            (field.letters_only && /^[а-яА-ЯёЁa-zA-Z]*$/.test(processedValue)) ||
            (field.digits_only && /^\d*$/.test(processedValue)) ||
            (field.letters_only && field.digits_only)) {
            onChange(e, field.id);
            // Если тип поля не является файлом, закрыть модальное окно
            if (field.field_type?.type !== 'file') {
                setModalOpen(false);
            }
        }
    };

    // Update period state when fieldValues change (e.g., when loading from backend)
    useEffect(() => {
        if (isPeriodField && field?.id) {
            try {
                const parsed = parsePeriodValue(fieldValues?.[field.id]);
                setPeriodFrom(parsed?.from ? formatDateToRu(parsed.from) : '');
                setPeriodTo(parsed?.to ? formatDateToRu(parsed.to) : '');
            } catch (error) {
                console.error('Error parsing period value:', error);
                setPeriodFrom('');
                setPeriodTo('');
            }
        }
    }, [fieldValues?.[field?.id], isPeriodField, field?.id]);

    useEffect(() => {
        if (!field || !field.field_type) return;
        
        if (isPeriodField) {
            const isFilled = periodFrom && periodTo;
            setAllFieldsFilled(isFilled);
        } else {
            const isFieldFilled = field.field_type?.type === 'file' ? !!fieldValues?.[field.id] : !!fieldValues?.[field.id];
            setAllFieldsFilled(isFieldFilled);
        }
    }, [fieldValues, field, isPeriodField, periodFrom, periodTo, setAllFieldsFilled]);

    // Safe access to field properties
    if (!field || !field.field_type) {
        console.warn('TextFields: field or field_type is missing', field);
        return null;
    }

    const rawLabel = field.label || field.name;
    const displayLabel = rawLabel === 'БСО' ? 'БСО (при наличии)' : rawLabel;
    const shouldShowValue = isFocused || (fieldValues?.[field.id] && fieldValues[field.id] !== '0');

    const mandatoryFileNames = ['Чек ККТ', 'Договор/спортивный абонемент'];
    const isFileField = field.field_type?.type === 'file';
    const fileFieldRequired = mandatoryFileNames.includes(field.label || field.name);

    // Period field rendering (без InputMask, чтобы исключить падения библиотеки)
    if (isPeriodField) {
        const rawLabel = field.label || field.name;
        const displayLabel = rawLabel === 'БСО' ? 'БСО (при наличии)' : rawLabel;
        
        return (
            <>
                <TextField
                    className={styles.declarationInput}
                    label={`${displayLabel} с`}
                    name={`${field.name}_from`}
                    type="text"
                    value={periodFrom}
                    onChange={(e) => handlePeriodDateChange('from', e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    error={!!fieldErrors[field.id]}
                    helperText={fieldErrors[field.id] || 'Формат: ДД.ММ.ГГГГ'}
                    required={true}
                    InputLabelProps={{
                        style: { color: '#A1A1A1' }
                    }}
                    inputProps={{
                        style: {
                            color: periodFrom ? 'inherit' : 'transparent',
                        },
                    }}
                    sx={{ mb: 2 }}
                />
                <TextField
                    className={styles.declarationInput}
                    label={`${displayLabel} по`}
                    name={`${field.name}_to`}
                    type="text"
                    value={periodTo}
                    onChange={(e) => handlePeriodDateChange('to', e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    error={!!fieldErrors[field.id]}
                    helperText={fieldErrors[field.id] || 'Формат: ДД.ММ.ГГГГ'}
                    required={true}
                    InputLabelProps={{
                        style: { color: '#A1A1A1' }
                    }}
                    inputProps={{
                        style: {
                            color: periodTo ? 'inherit' : 'transparent',
                        },
                    }}
                />
            </>
        );
    }

    // Decide if we really need an input mask
    const shouldUseMask = field.field_type?.type === 'date' || !!field.mask;
    const rawValue = fieldValues?.[field.id];
    const stringValue = rawValue == null ? '' : String(rawValue);

    return (
        <>
            {isFileField ? (
                <FileUploadWithPreview
                    field={field}
                    value={fieldValues[field.id]}
                    onChange={(fileOrArray) => {
                        fileChange({ target: { value: fileOrArray } }, field);
                    }}
                    error={fieldErrors[field.id]}
                    required={fileFieldRequired}
                />
            ) : shouldUseMask ? (
                <InputMask
                    mask={field.field_type?.type === 'date' ? '99.99.9999' : (field.mask || '')}
                    value={stringValue}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleFieldChange}
                    maskChar=""
                >
                    {(inputProps) => (
                        <TextField
                            key={field.name}
                            className={styles.declarationInput}
                            label={displayLabel}
                            name={field.name}
                            type={field.field_type?.type === 'date' ? 'text' : (field.field_type?.type || 'text')}
                            select={field.default_values?.length !== 0}
                            value={
                                field.default_values?.length !== 0
                                    ? modalOpen
                                        ? ''
                                        : stringValue
                                    : stringValue
                            }
                            onChange={handleFieldChange}
                            onClick={handleDropdownToggle}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            error={!!fieldErrors?.[field.id]}
                            helperText={
                                fieldErrors?.[field.id] ||
                                (field.field_type?.type === 'date' ? 'Формат: ДД.ММ.ГГГГ' : null)
                            }
                            required={true}
                            InputLabelProps={{
                                style: {
                                    color: '#A1A1A1',
                                },
                            }}
                            inputProps={{
                                style: {
                                    color:
                                        field.field_type?.type === 'date'
                                            ? shouldShowValue
                                                ? 'inherit'
                                                : 'transparent'
                                            : 'inherit',
                                },
                            }}
                            {...inputProps}
                        >
                            {(field.default_values || []).map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                    {option.value}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </InputMask>
            ) : (
                <TextField
                    key={field.name}
                    className={styles.declarationInput}
                    label={displayLabel}
                    name={field.name}
                    type={field.field_type?.type || 'text'}
                    select={field.default_values?.length !== 0}
                    value={
                        field.default_values?.length !== 0
                            ? modalOpen
                                ? ''
                                : stringValue
                            : stringValue
                    }
                    onChange={handleFieldChange}
                    onClick={handleDropdownToggle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    error={!!fieldErrors?.[field.id]}
                    helperText={fieldErrors?.[field.id] || null}
                    required={true}
                    InputLabelProps={{
                        style: {
                            color: '#A1A1A1',
                        },
                    }}
                    inputProps={{
                        style: {
                            color: 'inherit',
                        },
                    }}
                >
                    {(field.default_values || []).map((option) => (
                        <MenuItem key={option.id} value={option.value}>
                            {option.value}
                        </MenuItem>
                    ))}
                </TextField>
            )}
        </>
    );
};

export default TextFieldComponent;